import { Log } from '@shared/debug/log';
import { ProjectModel } from '../model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleNode } from '@project/components/article/presenter/article-node';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectVO } from '../model/vo/project.vo';
import { UserModel } from '@user/model/user.model';
import { ArrayUtils } from '@shared/util/array-utils';
import { Injectable } from '@angular/core';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { IsProjectType } from '../model/is-project-type';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class ProjectItemListHelper {
  public ITEM_LIST_PREFIX: string = 'ItemListPf_';

  constructor(
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private containerService: ContainerService,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private articleUsageService: ArticleUsageService
  ) {}

  public getItemNodeList(): ArticleNode[] {
    const compositeArticleIds: string[] = [];
    const itemListNodes: ArticleNode[] = [];
    const rootIds: string[] = [];
    const rootNodes: ArticleNode[] = this.createRootArticleNodes(this.projectModel.currentProject, compositeArticleIds);
    const articleNumber2UsagesMap: Map<string, string[]> = new Map<string, string[]>();
    const articleUsages: ArticleUsageVO[] = ProjectUtils.articleUsages(this.projectModel.currentProject);
    while (rootNodes && rootNodes.length > 0) {
      const node = rootNodes.pop();
      if (!Log.assertExists(node) || !Log.assertExists(node.articleUsage)) continue;
      if (articleNumber2UsagesMap.has(node.articleNo)) {
        articleNumber2UsagesMap.get(node.articleNo)!.push(node.articleUsage.groupId ?? node.articleUsage.id);
      } else {
        node.children = [];
        itemListNodes.push(node);
        articleNumber2UsagesMap.set(node.articleNo, [node.articleUsage.groupId ?? node.articleUsage.id]);
      }
      rootIds.push(node.articleUsage.id);
    }
    for (const articleUsage of articleUsages) {
      if (rootIds.includes(articleUsage.id)) continue;

      const node = this.createArticleNode(articleUsage, undefined, compositeArticleIds);
      if (!node) continue;

       const articleKey = node.articleOrCustomArticleNo;

      if (articleNumber2UsagesMap.has(articleKey)) {
        articleNumber2UsagesMap.get(articleKey)!.push(node.articleUsage?.groupId ?? node.articleUsage!.id);
      } else {
        itemListNodes.push(node);
        articleNumber2UsagesMap.set(articleKey, [node.articleUsage?.groupId ?? node.articleUsage!.id]);
      }
    }

    if(IsProjectType.mcsGds(this.projectModel.currentProject)) {
      itemListNodes.splice(0, 1);
    }

    this.populateUsagesInfo(itemListNodes, articleNumber2UsagesMap);

    itemListNodes.sort((a: ArticleNode, b: ArticleNode) => StringUtils.stringCompare(a.article.articleNo, b.article.articleNo));
    return itemListNodes;
  }

  private populateUsagesInfo(itemListNodes: ArticleNode[], articleNumber2UsagesMap: Map<string, string[]>) {
    for (const articleNode of itemListNodes) {
      const usageIds = articleNumber2UsagesMap.get(articleNode.articleOrCustomArticleNo)!;
      // For wire bridges this list contains identical groupIds for each single connection.
      // Make sure identical groupIds are not counted multiple times.
      const uniqueUsageIds = Array.from(new Set(usageIds));
      if (ArrayUtils.isNotEmpty(uniqueUsageIds)) {
        articleNode.articleListArticleUsageIds = uniqueUsageIds!;
        articleNode.id = this.ITEM_LIST_PREFIX + articleNode.id;
        articleNode.positionLabel = uniqueUsageIds.length.toString();
      }
    }
  }

  public createRootArticleNodes(project: ProjectVO, compositeArticleIds: string[] = []): ArticleNode[] {
    const rootNodes: ArticleNode[] = [];
    const articleUsagesInProject: ArticleUsageVO[] = ProjectUtils.articleUsages(project);
    for (const articleUsageInProject of articleUsagesInProject) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageInProject.id);
      if (!Log.assertExists(articleContainer)) continue;
      if (ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsageInProject).length !== 0) continue;

      const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(articleContainer.articleGUID);
      if (!Log.assertExists(articleUsage)) continue;

      const articleNode = this.createArticleNode(articleUsage, undefined, compositeArticleIds);
      if (articleNode) {
        rootNodes.push(articleNode);
      }
    }
    return rootNodes;
  }

  private createArticleNode(articleUsage: ArticleUsageVO, parentNode: ArticleNode | undefined, compositeArticleIds: string[]): ArticleNode | undefined {
    if (ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
      return this.createCompositeArticleNode(articleUsage, parentNode, compositeArticleIds);
    }
    return this.createDefaultArticleNode(articleUsage, parentNode);
  }

  private createDefaultArticleNode(articleUsage: ArticleUsageVO, parentNode: ArticleNode | undefined): ArticleNode | undefined {
    let article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);

    // MCS/GDS: use the project's base article instead of the freemounting article used by the project.
    if (IsProjectType.withBaseArticle(this.projectModel.currentProjectType)
      && ArticleUtils.isFreeMounting(article)
      && this.projectModel.baseArticle) {
        article = this.projectModel.baseArticle;
    }

    let articleNode: ArticleNode | undefined = undefined;
    if (article && !ArticleUtils.isDrillingHole(article)) {
      articleNode = new ArticleNode(
        this.containerService,
        articleUsage.groupId ? articleUsage.groupId : articleUsage.id,
        parentNode,
        article,
        articleUsage,
        this.articleUsageService.isArticleUsageDeletable(articleUsage) &&
        ProjectUtils.isProjectEditable(this.projectModel.currentProject, this.userModel.user),
        false,
        false
      );
      articleNode.isArticleListNode = true;
    }
    return articleNode;
  }

  private createCompositeArticleNode(articleUsage: ArticleUsageVO, parentNode: ArticleNode | undefined, compositeArticleIds: string[]): ArticleNode | undefined {
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    const compositeArticle: ArticleVO = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(container);
    const compositeArticleUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(container);
    if (compositeArticleIds.indexOf(compositeArticleUsageId) < 0) {
      // this ensures that each composite article appears only once in the article list
      compositeArticleIds.push(compositeArticleUsageId);
      let articleNode = new ArticleNode(
        this.containerService,
        CompositeArticleInfoUtils.getCompositeArticleUsageId(container),
        parentNode,
        compositeArticle,
        articleUsage,
        false,
        true,
        false
      );
      articleNode.isArticleListNode = true;
      return articleNode;
    }
  }
}
